import * as React from "react";

import {
  ActionPrimary,
  InitialStateMinimumRequirements_Recaptcha_ForAnyParticularConfigurationIsland,
  PasswordWithVisibilityToggleInput,
  SingleLineTextInput,
} from "../../form";

import { useScrollToErrorInForm } from "../../hooks";

import {
  LoginFormLogic,
  OneTimePasswordFeatures,
  OneTimePasswordFieldSuggestionCustomerErrorMessageToLookFor,
  OneTimePasswordUserExperience_Step,
  useFeatures,
  useLoginForm,
  useOneTimePasswordFieldSuggestion,
  useOneTimePasswordUserExperience,
} from "../../providers";

import { ProminentErrorMessageBox } from "../../prominent-error";

import { InitialStateMinimumRequirements_ForgotPasswordOptionInJoining } from "../initial-state-requirements";

import { TroubleSigningInSection } from "./trouble-signing-in-section";
import { ReCaptcha } from "../../recaptcha";

export interface LoginFormUiProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_ForgotPasswordOptionInJoining & {
      login_form_recaptcha: InitialStateMinimumRequirements_Recaptcha_ForAnyParticularConfigurationIsland;
    };
  };
}
export const LoginFormUi: React.FunctionComponent<LoginFormUiProps> = (
  props
) => {
  const { initialState } = props.useInitialState();
  const { login_form_recaptcha } = initialState || {};

  const {
    form: loginForm,
    onFormFieldFocus,
    onFormInputChange,
    onFormInputBlur,
    login,
  } = useLoginForm();

  const { handleStartTheUserFlowForAOneTimeCodeBasedLogin } =
    useOneTimePasswordFieldSuggestion();

  const { presentingUserExperienceStep } = useOneTimePasswordUserExperience();

  const [featureOneTimePassword] = useFeatures([
    OneTimePasswordFeatures.OneTimePassword,
  ]);

  useScrollToErrorInForm({
    formLogic: LoginFormLogic,
    form: loginForm,
    cssSelectorBase: ".content .page-login form ",
  });

  const showPasswordFieldOneTimePasswordSuggestion = React.useMemo(() => {
    return (
      !!featureOneTimePassword?.enabled &&
      loginForm.passwordError ===
        OneTimePasswordFieldSuggestionCustomerErrorMessageToLookFor
    );
  }, [featureOneTimePassword, loginForm]);

  const isLoginButtonDisabled = React.useMemo(() => {
    let areAllTheFieldsFilledIn =
      (loginForm?.email?.length || 0) > 0 &&
      (loginForm?.password?.length || 0) > 0;

    return !areAllTheFieldsFilledIn;
  }, [loginForm]);

  const onLoginClick: React.MouseEventHandler<HTMLButtonElement> = async (
    event
  ) => {
    const isRecaptchaV3 = initialState.login_form_recaptcha?.recaptchaEnabled as unknown as string === 'reCAPTCHA v3 Invisible';

    if (isRecaptchaV3 && (window as any).grecaptcha) {
      try {
        const captchaWrapperId = initialState.login_form_recaptcha?.recaptchaId + "-react-wrapper";
        const recaptchaElement = (document.querySelector("#" + captchaWrapperId)?.querySelector(".g-recaptcha-response")) as HTMLTextAreaElement;

        const token = await (window as any).grecaptcha.execute(
            (window as any).recaptchaSiteKey,
            {action: "validate_captcha"}
        );

        if (recaptchaElement && token) {
          recaptchaElement.value = token;
        }
      } catch (e) {
        console.log(e);
      }
    }

    event.preventDefault();
    login();
  };

  const handleCancelSubmit: React.MouseEventHandler<HTMLFormElement> =
    React.useCallback((event) => {
      event.preventDefault();
    }, []);

  return (
    <form className="bbnt-common-login-form-ui" onSubmit={handleCancelSubmit}>
      <SingleLineTextInput
        name="email"
        displayName="Email Address"
        form={loginForm}
        required
        onFocus={onFormFieldFocus}
        onChange={onFormInputChange}
        onBlur={onFormInputBlur}
      />

      <PasswordWithVisibilityToggleInput
        name="password"
        displayName="Password"
        form={loginForm}
        required
        onFocus={onFormFieldFocus}
        onChange={onFormInputChange}
        onBlur={onFormInputBlur}
        isCustomError={() => showPasswordFieldOneTimePasswordSuggestion}
        doesCustomErrorExtendStandardErrorState={() =>
          showPasswordFieldOneTimePasswordSuggestion
        }
        customErrorComponent={() => (
          <p className="error-message">
            <span>{`Your password is invalid. Please try again or `}</span>
            <span
              className="pretend-link sign-in-with-a-one-time-code-link"
              onClick={handleStartTheUserFlowForAOneTimeCodeBasedLogin}
            >{`sign in with a one-time code.`}</span>
          </p>
        )}
      />

      <ReCaptcha
        recaptchaConfiguration={login_form_recaptcha}
        name="recaptcha"
        form={loginForm}
        onChange={onFormInputChange}
      />

      {presentingUserExperienceStep ===
        OneTimePasswordUserExperience_Step.NOT_PRESENTING && (
        <ProminentErrorMessageBox />
      )}

      <ActionPrimary
        name="login"
        onClick={onLoginClick}
        disabled={isLoginButtonDisabled}
      >
        SIGN IN
      </ActionPrimary>

      <TroubleSigningInSection useInitialState={props.useInitialState} />
    </form>
  );
};
